import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  Badge,
  ErrorBox,
  formatDay,
  formatPartialDate,
  INSCRIPTION_STATUS,
  OUTCOME_LABELS,
  QUALITY_LABELS,
} from '../components/common';
import { Icon } from '../components/icons';
import { formatDate } from '../labels';
import '../styles/registre.css';
import { AffectedPanel, CoveragePanel, PaperSearchPanel } from './Delivery';
import { useReferentials } from './Workspace';

// ---------- Types renvoyés par l'API du registre ----------

interface Sentence {
  nature: string;
  durationMonths?: number;
  amountXaf?: number;
  suspended?: boolean;
  suspendedMonths?: number;
  conditions?: string;
}
interface Content {
  qualification?: string;
  sentences?: Sentence[];
  observations?: string;
  efface?: boolean;
}
interface InscriptionRow {
  id: string;
  status: string;
  reference: string;
  decided_on: string;
  jurisdiction_name: string;
  category_label: string;
  person_id: string;
  surname: string | null;
  given_names: string | null;
  prepared_by_name: string;
}
interface InscriptionDetail {
  id: string;
  status: string;
  person_id: string;
  prepared_by: string;
  prepared_by_name: string;
  validated_by_name: string | null;
  validated_at: string | null;
  decision_reason: string | null;
  decision_id: string;
  reference: string;
  decided_on: string;
  nature: string;
  legal_status: string;
  source_ref: string;
  jurisdiction_id: string;
  jurisdiction_name: string;
  holding_jurisdiction_name: string | null;
  category_label: string;
  category_legal_basis: string;
  person: {
    surname: string | null;
    given_names: string | null;
    birth_year: number | null;
    birth_month: number | null;
    birth_day: number | null;
    birth_place: string | null;
  } | null;
  versions: {
    version_no: number;
    content: Content;
    effective_from: string;
    reason: string;
    recorded_at: string;
    status: string;
    recorded_by: string;
    recorded_by_name: string;
    validated_by_name: string | null;
    decision_reason: string | null;
  }[];
  events: {
    id: string;
    event_label: string;
    effective_date: string;
    authority: string;
    evidence_ref: string;
    status: string;
    recorded_by: string;
    recorded_by_name: string;
  }[];
  pendingErasures: {
    id: string;
    legal_basis: string;
    reason: string;
    requested_by: string;
    requested_at: string;
  }[];
}
interface Candidate {
  personId: string;
  score: number;
  reasons: string[];
  contradictions: string[];
  surname: string | null;
  givenNames: string | null;
  birth: { year: number | null; month: number | null; day: number | null };
  birthPlace: string | null;
}
interface SearchResult {
  outcome: string;
  perimeter: string;
  candidates: Candidate[];
}
interface RegistryRefs {
  categories: { code: string; label: string; legal_basis: string; status: string }[];
  eventTypes: { code: string; label: string }[];
}

const LEGAL_STATUS: Record<string, string> = {
  non_definitive: 'Non définitive',
  definitive: 'Définitive',
  opposition: 'Frappée d’opposition',
  appel: 'Frappée d’appel',
  inconnu: 'Statut inconnu',
};

const VERSION_STATUS: Record<string, string> = {
  proposee: 'Proposée',
  validee: 'Validée',
  rejetee: 'Rejetée',
  effacee: 'Effacée',
};
const EVENT_STATUS: Record<string, string> = {
  soumis: 'À valider',
  valide: 'Validé',
  rejete: 'Rejeté',
  efface: 'Effacé',
};

function useRegistryRefs(): RegistryRefs | null {
  const [refs, setRefs] = useState<RegistryRefs | null>(null);
  useEffect(() => {
    api<RegistryRefs>('/registre/referentiels')
      .then(setRefs)
      .catch(() => setRefs(null));
  }, []);
  return refs;
}

function usePermissions() {
  const { me } = useAuth();
  return (p: string) => me?.permissions.includes(p) ?? false;
}

/** Statut sous forme de puce (maquette « Sable & okoumé ») ; même libellé et même ton que le badge. */
function StatusChip({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span className={tone === 'muted' ? 'chip' : `chip ${tone}`}>{children}</span>;
}

function sentenceText(s: Sentence): string {
  const parts = [s.nature];
  if (s.durationMonths !== undefined) parts.push(`${s.durationMonths} mois`);
  if (s.amountXaf !== undefined) parts.push(`${s.amountXaf.toLocaleString('fr-FR')} XAF`);
  if (s.suspended) parts.push(`avec sursis${s.suspendedMonths ? ` (${s.suspendedMonths} mois)` : ''}`);
  if (s.conditions) parts.push(s.conditions);
  return parts.join(' - ');
}

function ContentView({ content }: { content: Content }) {
  if (content.efface) return <p className="muted">Contenu effacé (effacement légal).</p>;
  return (
    <div>
      <strong>{content.qualification}</strong>
      <ul className="small" style={{ margin: '4px 0' }}>
        {(content.sentences ?? []).length === 0 && <li className="muted">Aucune peine saisie</li>}
        {(content.sentences ?? []).map((s, i) => (
          <li key={i}>{sentenceText(s)}</li>
        ))}
      </ul>
      {content.observations && <p className="small muted">{content.observations}</p>}
    </div>
  );
}

// ---------- Recherche d'identité (partagée) ----------

export interface IdentityForm {
  surname: string;
  givenNames: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  birthPlace: string;
  birthCountry: string;
  fatherName: string;
  motherName: string;
  sex: string;
}
export const EMPTY_IDENTITY: IdentityForm = {
  surname: '',
  givenNames: '',
  birthYear: '',
  birthMonth: '',
  birthDay: '',
  birthPlace: '',
  birthCountry: '',
  fatherName: '',
  motherName: '',
  sex: '',
};

export function identityPayload(f: IdentityForm) {
  const n = (v: string) => (v.trim() ? Number(v) : undefined);
  const s = (v: string) => (v.trim() ? v.trim() : undefined);
  return {
    surname: s(f.surname),
    givenNames: s(f.givenNames),
    birthYear: n(f.birthYear),
    birthMonth: n(f.birthMonth),
    birthDay: n(f.birthDay),
    birthPlace: s(f.birthPlace),
    birthCountry: s(f.birthCountry),
    fatherName: s(f.fatherName),
    motherName: s(f.motherName),
    sex: f.sex || undefined,
  };
}

export function IdentityFields({
  value,
  onChange,
  full,
}: {
  value: IdentityForm;
  onChange: (v: IdentityForm) => void;
  full?: boolean;
}) {
  const field = (key: keyof IdentityForm, label: string, extra: Record<string, unknown> = {}) => (
    <div className="field">
      <label htmlFor={`id-${key}`}>{label}</label>
      <input
        id={`id-${key}`}
        value={value[key]}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        {...extra}
      />
    </div>
  );
  return (
    <div className="form-grid">
      {field('surname', 'Nom')}
      {field('givenNames', 'Prénoms')}
      {field('birthYear', 'Année de naissance', { inputMode: 'numeric', placeholder: 'AAAA' })}
      {field('birthMonth', 'Mois (si connu)', { inputMode: 'numeric', placeholder: '1 à 12' })}
      {field('birthDay', 'Jour (si connu)', { inputMode: 'numeric', placeholder: '1 à 31' })}
      {field('birthPlace', 'Lieu de naissance')}
      {full && field('birthCountry', 'Pays de naissance')}
      {field('fatherName', 'Père (si nécessaire)')}
      {field('motherName', 'Mère (si nécessaire)')}
      {full && (
        <div className="field">
          <label htmlFor="id-sex">Sexe</label>
          <select id="id-sex" value={value.sex} onChange={(e) => onChange({ ...value, sex: e.target.value })}>
            <option value="">Non renseigné</option>
            <option value="F">F</option>
            <option value="M">M</option>
          </select>
        </div>
      )}
    </div>
  );
}

function SearchResults({
  result,
  onPick,
  pickLabel,
}: {
  result: SearchResult;
  onPick?: (c: Candidate) => void;
  pickLabel?: string;
}) {
  const o = OUTCOME_LABELS[result.outcome] ?? { label: result.outcome, tone: 'muted', help: '' };
  return (
    <div className="stack">
      <div
        className={`alert alert-${o.tone === 'muted' ? 'info' : o.tone === 'danger' ? 'danger' : o.tone === 'warn' ? 'warn' : 'info'}`}
        role="status"
      >
        <strong>{o.label}.</strong> {o.help}
        <div className="small" style={{ marginTop: 4 }}>
          {result.perimeter}
        </div>
      </div>
      {result.candidates.length > 0 && (
        <div className="card np table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Personne</th>
                <th scope="col">Naissance</th>
                <th scope="col">Ressemblance (aide au tri)</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {result.candidates.map((c) => (
                <tr key={c.personId}>
                  <td>
                    <Link to={`/espace/registre/personnes/${c.personId}`}>
                      {c.surname} {c.givenNames}
                    </Link>
                  </td>
                  <td>
                    {formatPartialDate(c.birth)}
                    {c.birthPlace ? ` - ${c.birthPlace}` : ''}
                  </td>
                  <td className="small">
                    <div>
                      {Math.round(c.score * 100)} %
                      {c.reasons.length > 0 && <div>Concordances : {c.reasons.join(', ')}</div>}
                      {c.contradictions.length > 0 && (
                        <div style={{ color: 'var(--danger)' }}>
                          Divergences : {c.contradictions.join(', ')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {onPick && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onPick(c)}>
                        {pickLabel ?? 'Choisir'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IdentitySearch({
  onPick,
  pickLabel,
  embedded,
  extraActions,
}: {
  onPick?: (c: Candidate) => void;
  pickLabel?: string;
  /** Formulaire intégré à une carte existante (nouvelle inscription) : pas de seconde carte. */
  embedded?: boolean;
  extraActions?: React.ReactNode;
}) {
  const [form, setForm] = useState<IdentityForm>(EMPTY_IDENTITY);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      setResult(
        await api<SearchResult>('/registre/personnes/recherche', {
          method: 'POST',
          body: identityPayload(form),
        }),
      );
    } catch (err) {
      setError(err as ApiError);
    }
  };
  return (
    <div className="stack">
      <form className={embedded ? 'stack' : 'card flow'} onSubmit={submit}>
        <IdentityFields value={form} onChange={setForm} />
        <div className="ch">
          <p className="muted small" style={{ margin: 0, maxWidth: 560 }}>
            Chaque recherche est journalisée avec sa finalité (tenue du casier). Le score est une aide au tri,
            jamais une preuve.
          </p>
          <div className="actions">
            <button className="btn" type="submit">
              Rechercher
            </button>
            {extraActions}
          </div>
        </div>
      </form>
      <ErrorBox error={error} />
      {result && <SearchResults result={result} onPick={onPick} pickLabel={pickLabel} />}
    </div>
  );
}

// ---------- Listes ----------

const STATUS_FILTERS = [
  { value: 'soumise', label: 'À valider' },
  { value: 'brouillon', label: 'Brouillons' },
  { value: 'a_corriger', label: 'À corriger' },
  { value: 'validee', label: 'Validées' },
  { value: 'rejetee', label: 'Rejetées' },
  { value: '', label: 'Toutes' },
];

export function InscriptionsPage({ title, intro }: { title: string; intro: string }) {
  const can = usePermissions();
  const [params, setParams] = useSearchParams();
  const filter = params.get('statut') ?? (can('inscription.valider') ? 'soumise' : '');
  const [rows, setRows] = useState<InscriptionRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    setRows(null);
    api<InscriptionRow[]>(`/registre/inscriptions${filter ? `?statut=${filter}` : ''}`)
      .then(setRows)
      .catch(setError);
  }, [filter]);

  // Compteurs calculés uniquement sur la liste déjà chargée (filtre « Toutes ») : aucun appel supplémentaire.
  const count = (status: string) => (rows ?? []).filter((r) => r.status === status).length;
  const showKpis = filter === '' && rows !== null && rows.length > 0;

  return (
    <div className="stack reg">
      <div className="head">
        <div>
          <h1>{title}</h1>
          <p className="lead">{intro}</p>
        </div>
        {can('inscription.preparer') && (
          <div className="actions">
            <Link className="btn" to="/espace/registre/nouvelle">
              Nouvelle inscription
            </Link>
          </div>
        )}
      </div>
      {showKpis && (
        <div className="kpis">
          <div className="stat">
            <span className="v">{count('soumise')}</span>
            <span className="l">À valider par un tiers</span>
          </div>
          <div className="stat">
            <span className="v">{count('validee')}</span>
            <span className="l">Validées</span>
          </div>
          <div className="stat">
            <span className="v">{count('a_corriger')}</span>
            <span className="l">À corriger</span>
          </div>
        </div>
      )}
      <div className="ch">
        <div className="tabs" role="group" aria-label="Filtrer par statut">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className="tab"
              aria-pressed={filter === f.value}
              onClick={() => setParams({ statut: f.value })}
            >
              {f.label}
              {filter === f.value && rows !== null && <span className="n">{rows.length}</span>}
            </button>
          ))}
        </div>
      </div>
      <ErrorBox error={error} />
      {rows !== null && rows.length === 0 ? (
        <div className="card np">
          <div className="empty">
            <span className="ic">
              <Icon name="registry" size={22} />
            </span>
            <b>Aucune inscription</b>
            <span>Aucune inscription dans cette catégorie et dans votre périmètre.</span>
          </div>
        </div>
      ) : (
        <div className="card np table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Personne</th>
                <th scope="col">Décision</th>
                <th scope="col">Juridiction</th>
                <th scope="col">Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr>
                  <td colSpan={4}>Chargement…</td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/espace/registre/inscriptions/${r.id}`}>
                        {r.surname} {r.given_names}
                      </Link>
                    </td>
                    <td>
                      <div>
                        <span className="mono">{r.reference}</span>
                        <div className="small muted">
                          {r.category_label} - {formatDay(r.decided_on)}
                        </div>
                      </div>
                    </td>
                    <td className="small">{r.jurisdiction_name}</td>
                    <td>
                      <StatusChip tone={INSCRIPTION_STATUS[r.status]?.tone ?? 'muted'}>
                        {INSCRIPTION_STATUS[r.status]?.label ?? r.status}
                      </StatusChip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function CasierCentralPage() {
  const can = usePermissions();
  const tab = useSearchParams()[0].get('onglet') ?? 'recherche';
  return (
    <div className="stack reg">
      <div className="head">
        <div>
          <h1>Casier central</h1>
          <p className="lead">Recherche d’identité, inscriptions, effacements et couverture documentaire.</p>
        </div>
      </div>
      <nav className="tabs" aria-label="Onglets">
        <Link className="tab" aria-current={tab === 'recherche' ? 'page' : undefined} to="?onglet=recherche">
          Recherche d’identité
        </Link>
        {can('identite.resoudre') && (
          <Link
            className="tab"
            aria-current={tab === 'operations' ? 'page' : undefined}
            to="?onglet=operations"
          >
            Fusions et séparations
          </Link>
        )}
        {can('casier.controler') && (
          <>
            <Link
              className="tab"
              aria-current={tab === 'inscriptions' ? 'page' : undefined}
              to="?onglet=inscriptions"
            >
              Inscriptions
            </Link>
            <Link
              className="tab"
              aria-current={tab === 'effacements' ? 'page' : undefined}
              to="?onglet=effacements"
            >
              Registre des effacements
            </Link>
            <Link
              className="tab"
              aria-current={tab === 'couverture' ? 'page' : undefined}
              to="?onglet=couverture"
            >
              Couverture documentaire
            </Link>
            <Link
              className="tab"
              aria-current={tab === 'recherches' ? 'page' : undefined}
              to="?onglet=recherches"
            >
              Recherches papier
            </Link>
            <Link
              className="tab"
              aria-current={tab === 'concernes' ? 'page' : undefined}
              to="?onglet=concernes"
            >
              Bulletins concernés
            </Link>
            <Link className="tab" to="/espace/demandes/signalements">
              Signalements des usagers
            </Link>
          </>
        )}
      </nav>
      {tab === 'recherche' && <IdentitySearch />}
      {tab === 'operations' && <OperationsPanel />}
      {tab === 'inscriptions' && (
        <InscriptionsPage title="Inscriptions" intro="Toutes les inscriptions de votre périmètre national." />
      )}
      {tab === 'effacements' && <ErasuresPanel />}
      {tab === 'couverture' && <CoveragePanel />}
      {tab === 'recherches' && <PaperSearchPanel />}
      {tab === 'concernes' && <AffectedPanel />}
    </div>
  );
}

interface Operation {
  id: string;
  kind: 'fusion' | 'separation';
  source_person_id: string;
  target_person_id: string;
  status: string;
  reason: string;
  evidence: string;
  requested_by: string;
  requested_at: string;
  decision_reason: string | null;
}

function OperationsPanel() {
  const { me } = useAuth();
  const [ops, setOps] = useState<Operation[]>([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const load = () => api<Operation[]>('/registre/operations-identite').then(setOps).catch(setError);
  useEffect(() => {
    void load();
  }, []);
  const decide = async (id: string, verb: 'approuver' | 'refuser') => {
    setError(null);
    try {
      await api(`/registre/operations-identite/${id}/${verb}`, { method: 'POST', body: { reason } });
      setReason('');
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };
  return (
    <div className="stack">
      <p className="muted">
        Une fusion ou une séparation est demandée par un responsable et approuvée par un autre. Une fusion
        erronée se répare par une séparation, qui réaffecte identités et inscriptions.
      </p>
      <div className="field">
        <label htmlFor="op-reason">Motif de décision</label>
        <input id="op-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <ErrorBox error={error} />
      <div className="card np table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Opération</th>
              <th scope="col">Personnes</th>
              <th scope="col">Motif et sources</th>
              <th scope="col">Statut</th>
            </tr>
          </thead>
          <tbody>
            {ops.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  Aucune opération.
                </td>
              </tr>
            )}
            {ops.map((o) => (
              <tr key={o.id}>
                <td>
                  <div>
                    {o.kind === 'fusion' ? 'Fusion' : 'Séparation'}
                    <div className="small muted">{formatDate(o.requested_at)}</div>
                  </div>
                </td>
                <td className="small">
                  <div>
                    <Link to={`/espace/registre/personnes/${o.source_person_id}`}>
                      {o.kind === 'fusion' ? 'absorbée' : 'actuelle'}
                    </Link>
                    {' → '}
                    <Link to={`/espace/registre/personnes/${o.target_person_id}`}>
                      {o.kind === 'fusion' ? 'conservée' : 'rétablie'}
                    </Link>
                  </div>
                </td>
                <td className="small">
                  <div>
                    {o.reason}
                    <div className="muted">{o.evidence}</div>
                  </div>
                </td>
                <td>
                  {o.status === 'demandee' ? (
                    o.requested_by === me?.userId ? (
                      <span className="small muted">En attente d’un autre responsable</span>
                    ) : (
                      <div className="row">
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={reason.trim().length < 5}
                          onClick={() => void decide(o.id, 'approuver')}
                        >
                          Approuver
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={reason.trim().length < 5}
                          onClick={() => void decide(o.id, 'refuser')}
                        >
                          Refuser
                        </button>
                      </div>
                    )
                  ) : (
                    <StatusChip tone={o.status === 'executee' ? 'ok' : 'muted'}>
                      {o.status === 'executee' ? 'Exécutée' : 'Refusée'}
                    </StatusChip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ErasuresPanel() {
  const [rows, setRows] = useState<
    {
      id: string;
      inscription_id: string;
      legal_basis: string;
      status: string;
      requested_at: string;
      executed_at: string | null;
    }[]
  >([]);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    api<typeof rows>('/registre/effacements').then(setRows).catch(setError);
  }, []);
  return (
    <div className="stack">
      <p className="muted">
        Le registre des effacements ne conserve que des identifiants et la référence de l’acte. Il est rejoué
        après toute restauration de sauvegarde, avant remise en service.
      </p>
      <ErrorBox error={error} />
      <div className="card np table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Inscription</th>
              <th scope="col">Fondement</th>
              <th scope="col">Statut</th>
              <th scope="col">Exécution</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  Aucun effacement.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <span className="mono small">{r.inscription_id.slice(0, 8)}…</span>
                </td>
                <td className="small">{r.legal_basis}</td>
                <td>
                  {r.status === 'executee' ? 'Exécuté' : r.status === 'demandee' ? 'Demandé' : 'Refusé'}
                </td>
                <td className="small">{r.executed_at ? formatDate(r.executed_at) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Fiche personne ----------

interface PersonDetail {
  id: string;
  status: string;
  mergedInto: string | null;
  holdingJurisdiction: { id: string; name: string };
  identities: {
    id: string;
    kind: string;
    surname: string | null;
    givenNames: string | null;
    sex: string | null;
    birth: { year: number | null; month: number | null; day: number | null };
    birthPlace: string | null;
    birthCountry: string | null;
    fatherName: string | null;
    motherName: string | null;
    quality: Record<string, string>;
    provenance: string;
    sourceRef: string | null;
  }[];
  verifications: {
    id: string;
    method: string;
    result: string;
    evidence_ref: string;
    verified_at: string;
    verified_by: string;
  }[];
  inscriptions: {
    id: string;
    status: string;
    reference: string;
    decided_on: string;
    jurisdiction_name: string;
    category_label: string;
  }[];
  hiddenInscriptions: number;
  operations: {
    id: string;
    kind: string;
    status: string;
    source_person_id: string;
    target_person_id: string;
    reason: string;
  }[];
}

export function PersonPage() {
  const { id } = useParams();
  const can = usePermissions();
  const [p, setP] = useState<PersonDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [verif, setVerif] = useState({ method: '', result: 'confirmee', evidenceRef: '' });
  const [merge, setMerge] = useState({ targetPersonId: '', reason: '', evidence: '' });
  const [sep, setSep] = useState({ reason: '', evidence: '' });

  const load = useCallback(
    () => api<PersonDetail>(`/registre/personnes/${id}`).then(setP).catch(setError),
    [id],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const post = async (path: string, body: unknown, done: string) => {
    setError(null);
    setNotice(null);
    try {
      await api(path, { method: 'POST', body });
      setNotice(done);
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!p) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  const main = p.identities.find((i) => i.kind === 'principale') ?? p.identities[0];
  const executedFusion = p.operations.find(
    (o) => o.kind === 'fusion' && o.status === 'executee' && o.target_person_id === p.id,
  );

  const fromCentral = can('casier.controler') || can('identite.resoudre');

  return (
    <div className="stack reg">
      <div className="head">
        <div>
          <p className="crumb">
            {fromCentral ? (
              <Link to="/espace/casier-central">‹ Casier central</Link>
            ) : (
              <Link to="/espace/juridictions">‹ Juridictions</Link>
            )}
          </p>
          <h1>
            {main?.surname} {main?.givenNames}
            {p.status === 'fusionnee' ? (
              <StatusChip tone="warn">Fusionnée</StatusChip>
            ) : (
              <StatusChip tone="ok">Active</StatusChip>
            )}
          </h1>
          <p className="lead">
            Identifiant CASIJUD : <span className="mono">{p.id}</span> · casier tenant :{' '}
            {p.holdingJurisdiction.name}
          </p>
        </div>
      </div>
      {p.mergedInto && (
        <p className="alert alert-warn">
          Cette personne a été fusionnée dans{' '}
          <Link to={`/espace/registre/personnes/${p.mergedInto}`}>une autre fiche</Link>.
        </p>
      )}
      <ErrorBox error={error} />
      {notice && <p className="alert alert-ok">{notice}</p>}

      <section className="card np" aria-labelledby="h-identites">
        <div className="card-h">
          <h2 id="h-identites">Identités</h2>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Nom et prénoms</th>
                <th scope="col">Naissance</th>
                <th scope="col">Filiation</th>
                <th scope="col">Provenance</th>
              </tr>
            </thead>
            <tbody>
              {p.identities.map((i) => (
                <tr key={i.id}>
                  <td>
                    {i.kind === 'principale' ? 'Principale' : i.kind === 'alias' ? 'Alias' : 'Variante'}
                  </td>
                  <td>
                    {i.surname ?? <span className="muted">-</span>} {i.givenNames}
                  </td>
                  <td>
                    <div>
                      {formatPartialDate(i.birth)}
                      <div className="small">
                        {i.birthPlace ?? (
                          <span className="muted">
                            lieu{' '}
                            {i.quality.birthPlace ? QUALITY_LABELS[i.quality.birthPlace] : 'non renseigné'}
                          </span>
                        )}
                        {i.birthCountry ? `, ${i.birthCountry}` : ''}
                      </div>
                      {Object.entries(i.quality).map(([k, v]) => (
                        <Badge key={k} tone={v === 'verifiee' ? 'ok' : 'warn'}>
                          {k} : {QUALITY_LABELS[v] ?? v}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="small">
                    <div>
                      {i.fatherName && <div>Père : {i.fatherName}</div>}
                      {i.motherName && <div>Mère : {i.motherName}</div>}
                    </div>
                  </td>
                  <td className="small">
                    <div>
                      {i.provenance}
                      {i.sourceRef && <div className="muted mono">{i.sourceRef}</div>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="cols">
        <section className="card flow" aria-labelledby="h-inscriptions">
          <h2 id="h-inscriptions">Inscriptions de votre périmètre</h2>
          {p.inscriptions.length === 0 ? (
            <p className="muted">Aucune inscription visible dans votre périmètre.</p>
          ) : (
            <ul className="item-list">
              {p.inscriptions.map((i) => (
                <li key={i.id}>
                  <Link className="card tint item" to={`/espace/registre/inscriptions/${i.id}`}>
                    <span className="ch">
                      <span className="mono" style={{ fontWeight: 600 }}>
                        {i.reference}
                      </span>
                      <StatusChip tone={INSCRIPTION_STATUS[i.status]?.tone ?? 'muted'}>
                        {INSCRIPTION_STATUS[i.status]?.label}
                      </StatusChip>
                    </span>
                    <span className="muted small">
                      {i.category_label} · {i.jurisdiction_name} · {formatDay(i.decided_on)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {p.hiddenInscriptions > 0 && (
            <p className="small muted">
              {p.hiddenInscriptions} autre(s) inscription(s) hors de votre périmètre.
            </p>
          )}
        </section>

        <section className="card flow" aria-labelledby="h-verifications">
          <h2 id="h-verifications">Vérifications d’identité</h2>
          {p.verifications.length === 0 ? (
            <p className="alert alert-warn">
              Aucune vérification enregistrée : l’identité n’est pas confirmée.
            </p>
          ) : (
            <ol className="tl">
              {p.verifications.map((v) => (
                <li key={v.id} className="done">
                  <b>
                    {v.method} : {v.result}
                  </b>
                  <span>
                    {formatDate(v.verified_at)} - {v.evidence_ref} - par {v.verified_by}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {(can('identite.resoudre') || can('casier.controler') || can('inscription.valider')) &&
            p.status === 'active' && (
              <form
                className="stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  void post(`/registre/personnes/${p.id}/verifications`, verif, 'Vérification enregistrée.');
                }}
              >
                <div className="fg">
                  <div className="field">
                    <label htmlFor="v-method">Méthode</label>
                    <input
                      id="v-method"
                      required
                      minLength={3}
                      value={verif.method}
                      onChange={(e) => setVerif({ ...verif, method: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="v-result">Résultat</label>
                    <select
                      id="v-result"
                      value={verif.result}
                      onChange={(e) => setVerif({ ...verif, result: e.target.value })}
                    >
                      <option value="confirmee">Confirmée</option>
                      <option value="incertaine">Incertaine</option>
                      <option value="infirmee">Infirmée</option>
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="v-proof">Pièce</label>
                    <input
                      id="v-proof"
                      required
                      minLength={3}
                      value={verif.evidenceRef}
                      onChange={(e) => setVerif({ ...verif, evidenceRef: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <button className="btn" type="submit">
                    Enregistrer la vérification
                  </button>
                </div>
              </form>
            )}
        </section>
      </div>

      {can('identite.resoudre') && p.status === 'active' && (
        <section className="card flow" aria-labelledby="h-rapprochement">
          <h2 id="h-rapprochement">Rapprochement</h2>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void post(
                '/registre/fusions',
                {
                  sourcePersonId: merge.targetPersonId,
                  targetPersonId: p.id,
                  reason: merge.reason,
                  evidence: merge.evidence,
                },
                'Fusion demandée : elle sera exécutée après approbation par un autre responsable.',
              );
            }}
          >
            <p className="muted small" style={{ margin: 0 }}>
              Demander que la fiche dont l’identifiant suit soit absorbée par celle-ci (double contrôle).
            </p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="m-id">Identifiant de la fiche à absorber</label>
                <input
                  id="m-id"
                  required
                  value={merge.targetPersonId}
                  onChange={(e) => setMerge({ ...merge, targetPersonId: e.target.value.trim() })}
                />
              </div>
              <div className="field">
                <label htmlFor="m-reason">Motif</label>
                <input
                  id="m-reason"
                  required
                  minLength={10}
                  value={merge.reason}
                  onChange={(e) => setMerge({ ...merge, reason: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="m-evidence">Sources</label>
                <input
                  id="m-evidence"
                  required
                  minLength={3}
                  value={merge.evidence}
                  onChange={(e) => setMerge({ ...merge, evidence: e.target.value })}
                />
              </div>
            </div>
            <div>
              <button className="btn btn-ghost" type="submit">
                Demander la fusion
              </button>
            </div>
          </form>
          {executedFusion && (
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                void post(`/registre/fusions/${executedFusion.id}/separer`, sep, 'Séparation demandée.');
              }}
            >
              <p className="muted small" style={{ margin: 0 }}>
                Cette fiche résulte d’une fusion. Si elle est erronée, demandez une séparation.
              </p>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="s-reason">Motif</label>
                  <input
                    id="s-reason"
                    required
                    minLength={10}
                    value={sep.reason}
                    onChange={(e) => setSep({ ...sep, reason: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="s-evidence">Sources</label>
                  <input
                    id="s-evidence"
                    required
                    minLength={3}
                    value={sep.evidence}
                    onChange={(e) => setSep({ ...sep, evidence: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <button className="btn btn-danger" type="submit">
                  Demander la séparation
                </button>
              </div>
            </form>
          )}
        </section>
      )}
    </div>
  );
}

// ---------- Nouvelle inscription ----------

export function NewInscriptionPage() {
  const { me } = useAuth();
  const refs = useReferentials();
  const regRefs = useRegistryRefs();
  const navigate = useNavigate();
  const [person, setPerson] = useState<{ id: string; label: string } | null>(null);
  const [newPerson, setNewPerson] = useState<IdentityForm>(EMPTY_IDENTITY);
  const [provenance, setProvenance] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [decision, setDecision] = useState({
    jurisdictionId: '',
    reference: '',
    decidedOn: '',
    categoryCode: 'condamnation_penale',
    nature: 'Jugement contradictoire',
    legalStatus: 'definitive',
    sourceRef: '',
  });
  const [existingDecision, setExistingDecision] = useState<{ id: string; reference: string } | null>(null);
  const [found, setFound] = useState<{ id: string; reference: string; decided_on: string }[]>([]);
  const [content, setContent] = useState({
    qualification: '',
    observations: '',
    effectiveFrom: '',
    reason: 'Saisie initiale',
  });
  const [sentences, setSentences] = useState<Sentence[]>([{ nature: 'emprisonnement' }]);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  // Juridictions où l'agent peut préparer.
  const allowed = (me?.habilitations ?? [])
    .filter((h) =>
      refs?.profiles.find((p) => p.code === h.profileCode)?.permissions.includes('inscription.preparer'),
    )
    .flatMap((h) =>
      h.scope === 'national' ? (refs?.jurisdictions ?? []).map((j) => j.id) : h.jurisdictionIds,
    );
  const jurisdictions = (refs?.jurisdictions ?? []).filter((j) => allowed.includes(j.id));
  useEffect(() => {
    if (!decision.jurisdictionId && jurisdictions[0])
      setDecision((d) => ({ ...d, jurisdictionId: jurisdictions[0]!.id }));
  }, [jurisdictions, decision.jurisdictionId]);

  const createPerson = async () => {
    setError(null);
    try {
      const created = await api<{ id: string }>('/registre/personnes', {
        method: 'POST',
        body: { ...identityPayload(newPerson), provenance },
      });
      setPerson({ id: created.id, label: `${newPerson.surname} ${newPerson.givenNames}` });
      setShowCreate(false);
    } catch (e) {
      setError(e as ApiError);
    }
  };

  const searchDecisions = async () => {
    if (!decision.jurisdictionId) return;
    try {
      setFound(
        await api(
          `/registre/decisions?juridiction=${decision.jurisdictionId}&reference=${encodeURIComponent(decision.reference)}`,
        ),
      );
    } catch (e) {
      setError(e as ApiError);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!person) return;
    setBusy(true);
    setError(null);
    try {
      const decisionId =
        existingDecision?.id ??
        (await api<{ id: string }>('/registre/decisions', { method: 'POST', body: decision })).id;
      const cleanSentences = sentences
        .filter((s) => s.nature.trim())
        .map((s) => Object.fromEntries(Object.entries(s).filter(([, v]) => v !== undefined && v !== '')));
      const created = await api<{ id: string }>('/registre/inscriptions', {
        method: 'POST',
        body: {
          decisionId,
          personId: person.id,
          content: {
            qualification: content.qualification,
            sentences: cleanSentences,
            ...(content.observations ? { observations: content.observations } : {}),
          },
          effectiveFrom: content.effectiveFrom || decision.decidedOn,
          reason: content.reason,
        },
      });
      navigate(`/espace/registre/inscriptions/${created.id}`);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const setSentence = (i: number, patch: Partial<Sentence>) =>
    setSentences((list) => list.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const num = (v: string) => (v === '' ? undefined : Number(v));

  return (
    <div className="stack reg">
      <div className="head">
        <div>
          <p className="crumb">
            <Link to="/espace/juridictions">‹ Juridictions</Link>
          </p>
          <h1>Préparer une inscription</h1>
          <p className="lead">
            Une inscription est individuelle : une personne, une décision, ses propres peines. Elle sera
            validée par une autre personne habilitée.
          </p>
        </div>
      </div>

      <section className="card flow" aria-labelledby="etape-personne">
        <div className="sect">
          <span className="num" aria-hidden="true">
            1
          </span>
          <h2 id="etape-personne">
            <span className="visually-hidden">1. </span>Personne concernée
          </h2>
        </div>
        {person ? (
          <div className="ch">
            <span>
              <strong>{person.label}</strong> - <code className="small">{person.id.slice(0, 8)}…</code>
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPerson(null)}>
              Changer
            </button>
          </div>
        ) : (
          <>
            <IdentitySearch
              embedded
              pickLabel="Retenir"
              onPick={(c) => setPerson({ id: c.personId, label: `${c.surname ?? ''} ${c.givenNames ?? ''}` })}
              extraActions={
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreate((v) => !v)}>
                  {showCreate ? 'Annuler la création' : 'Créer une nouvelle personne'}
                </button>
              }
            />
            {showCreate && (
              <div className="card tint flow">
                <p className="small muted" style={{ margin: 0 }}>
                  Laissez vide ce qui est inconnu : rien ne sera complété (année seule acceptée).
                </p>
                <IdentityFields value={newPerson} onChange={setNewPerson} full />
                <div className="field">
                  <label htmlFor="prov">Provenance (source des informations)</label>
                  <input
                    id="prov"
                    required
                    minLength={3}
                    value={provenance}
                    onChange={(e) => setProvenance(e.target.value)}
                  />
                </div>
                <div>
                  <button type="button" className="btn" onClick={() => void createPerson()}>
                    Créer la personne
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <form className="stack" onSubmit={submit}>
        <section className="card flow" aria-labelledby="etape-decision">
          <div className="sect">
            <span className="num" aria-hidden="true">
              2
            </span>
            <h2 id="etape-decision">
              <span className="visually-hidden">2. </span>Décision
            </h2>
          </div>
          {existingDecision ? (
            <div className="ch">
              <span>
                Décision existante : <strong>{existingDecision.reference}</strong>
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setExistingDecision(null)}
              >
                Changer
              </button>
            </div>
          ) : (
            <>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="d-jur">Juridiction</label>
                  <select
                    id="d-jur"
                    value={decision.jurisdictionId}
                    onChange={(e) => setDecision({ ...decision, jurisdictionId: e.target.value })}
                  >
                    {jurisdictions.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="d-ref">Référence</label>
                  <input
                    id="d-ref"
                    required
                    value={decision.reference}
                    onChange={(e) => setDecision({ ...decision, reference: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="d-date">Date de la décision</label>
                  <input
                    id="d-date"
                    type="date"
                    required
                    value={decision.decidedOn}
                    onChange={(e) => setDecision({ ...decision, decidedOn: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="d-cat">Catégorie</label>
                  <select
                    id="d-cat"
                    value={decision.categoryCode}
                    onChange={(e) => setDecision({ ...decision, categoryCode: e.target.value })}
                  >
                    {(regRefs?.categories ?? []).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="d-nature">Nature</label>
                  <input
                    id="d-nature"
                    required
                    value={decision.nature}
                    onChange={(e) => setDecision({ ...decision, nature: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="d-status">Statut juridique documenté</label>
                  <select
                    id="d-status"
                    value={decision.legalStatus}
                    onChange={(e) => setDecision({ ...decision, legalStatus: e.target.value })}
                  >
                    {Object.entries(LEGAL_STATUS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="d-src">Source (minute, extrait)</label>
                  <input
                    id="d-src"
                    required
                    minLength={3}
                    value={decision.sourceRef}
                    onChange={(e) => setDecision({ ...decision, sourceRef: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <button type="button" className="btn btn-ghost" onClick={() => void searchDecisions()}>
                  Chercher une décision existante (jugement à plusieurs personnes)
                </button>
              </div>
              {found.length > 0 && (
                <ul className="item-list">
                  {found.map((d) => (
                    <li key={d.id} className="card tint ch" style={{ padding: '12px 18px' }}>
                      <span>
                        <span className="mono">{d.reference}</span> - {formatDay(d.decided_on)}
                      </span>{' '}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setExistingDecision({ id: d.id, reference: d.reference })}
                      >
                        Rattacher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        <section className="card flow" aria-labelledby="etape-contenu">
          <div className="sect">
            <span className="num" aria-hidden="true">
              3
            </span>
            <h2 id="etape-contenu">
              <span className="visually-hidden">3. </span>Contenu de l’inscription
            </h2>
          </div>
          <div className="field">
            <label htmlFor="c-qual">Qualification</label>
            <input
              id="c-qual"
              required
              minLength={2}
              value={content.qualification}
              onChange={(e) => setContent({ ...content, qualification: e.target.value })}
            />
          </div>
          <fieldset className="sub">
            <legend>Peines et mesures propres à cette personne</legend>
            {sentences.map((s, i) => (
              <div key={i} className="fg">
                <div className="field">
                  <label htmlFor={`s-nature-${i}`}>Nature</label>
                  <input
                    id={`s-nature-${i}`}
                    value={s.nature}
                    onChange={(e) => setSentence(i, { nature: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor={`s-months-${i}`}>Durée (mois)</label>
                  <input
                    id={`s-months-${i}`}
                    inputMode="numeric"
                    value={s.durationMonths ?? ''}
                    onChange={(e) => setSentence(i, { durationMonths: num(e.target.value) })}
                  />
                </div>
                <div className="field">
                  <label htmlFor={`s-amount-${i}`}>Montant (XAF)</label>
                  <input
                    id={`s-amount-${i}`}
                    inputMode="numeric"
                    value={s.amountXaf ?? ''}
                    onChange={(e) => setSentence(i, { amountXaf: num(e.target.value) })}
                  />
                </div>
                <label className="checkbox end">
                  <input
                    type="checkbox"
                    checked={s.suspended ?? false}
                    onChange={(e) => setSentence(i, { suspended: e.target.checked || undefined })}
                  />
                  Avec sursis
                </label>
              </div>
            ))}
            <div className="actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setSentences([...sentences, { nature: '' }])}
              >
                Ajouter une peine
              </button>
              {sentences.length > 0 && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSentences(sentences.slice(0, -1))}
                >
                  Retirer la dernière
                </button>
              )}
            </div>
          </fieldset>
          <div className="fg">
            <div className="field">
              <label htmlFor="c-eff">Date d’effet (par défaut : date de la décision)</label>
              <input
                id="c-eff"
                type="date"
                value={content.effectiveFrom}
                onChange={(e) => setContent({ ...content, effectiveFrom: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="c-reason">Motif de la saisie</label>
              <input
                id="c-reason"
                required
                minLength={5}
                value={content.reason}
                onChange={(e) => setContent({ ...content, reason: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="c-obs">Observations</label>
            <textarea
              id="c-obs"
              value={content.observations}
              onChange={(e) => setContent({ ...content, observations: e.target.value })}
            />
          </div>
        </section>
        <ErrorBox error={error} />
        <div className="card tint foot">
          <button className="btn" type="submit" disabled={busy || !person}>
            {busy ? 'Enregistrement…' : 'Enregistrer le brouillon'}
          </button>
          {!person && <span className="small muted">Choisissez d’abord la personne.</span>}
        </div>
      </form>
    </div>
  );
}

// ---------- Détail d'une inscription ----------

export function InscriptionDetailPage() {
  const { id } = useParams();
  const { me } = useAuth();
  const can = usePermissions();
  const regRefs = useRegistryRefs();
  const [d, setD] = useState<InscriptionDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [event, setEvent] = useState({
    eventType: 'rehabilitation',
    effectiveDate: '',
    decidedOn: '',
    authority: '',
    evidenceRef: '',
  });
  const [erasure, setErasure] = useState({ legalBasis: '', reason: '' });
  const [state, setState] = useState<{ date: string; knownAt: string; result: unknown } | null>(null);
  const [asOf, setAsOf] = useState({ date: new Date().toISOString().slice(0, 10), knownAt: '' });
  const [correction, setCorrection] = useState({ qualification: '', effectiveFrom: '', reason: '' });

  const load = useCallback(
    () => api<InscriptionDetail>(`/registre/inscriptions/${id}`).then(setD).catch(setError),
    [id],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const post = async (path: string, body: unknown, done: string) => {
    setError(null);
    setNotice(null);
    try {
      await api(path, { method: 'POST', body });
      setNotice(done);
      setReason('');
      await load();
    } catch (e) {
      setError(e as ApiError);
    }
  };

  const viewState = async () => {
    setError(null);
    try {
      const q = `date=${asOf.date}${asOf.knownAt ? `&connuLe=${encodeURIComponent(new Date(asOf.knownAt).toISOString())}` : ''}`;
      setState({ ...asOf, result: await api(`/registre/inscriptions/${id}/etat?${q}`) });
    } catch (e) {
      setError(e as ApiError);
    }
  };

  if (!d) return error ? <ErrorBox error={error} /> : <p>Chargement…</p>;
  const st = INSCRIPTION_STATUS[d.status] ?? { label: d.status, tone: 'muted' };
  const validated = d.versions.filter((v) => v.status === 'validee');
  const current = validated[validated.length - 1] ?? d.versions[d.versions.length - 1];
  const pendingVersion = d.versions.find((v) => v.status === 'proposee' && d.status === 'validee');
  const canPrepare = can('inscription.preparer');
  const canValidate = can('inscription.valider');
  const reasonOk = reason.trim().length >= 5;
  const stateResult = state?.result as
    | {
        version: { version_no: number; content: Content } | null;
        events: { id: string; label: string; effective_date: string }[];
        notice: string;
      }
    | undefined;

  return (
    <div className="stack reg">
      <div className="head">
        <div>
          <p className="crumb">
            <Link to="/espace/juridictions">‹ Inscriptions</Link>
          </p>
          <h1>
            {d.person?.surname} {d.person?.given_names}
            <StatusChip tone={st.tone}>{st.label}</StatusChip>
          </h1>
          <p className="lead">
            Inscription <span className="mono">{d.reference}</span>
          </p>
        </div>
      </div>
      <ErrorBox error={error} />
      {notice && (
        <p className="alert alert-ok" role="status">
          {notice}
        </p>
      )}

      <div className="cols">
        <section className="card flow" aria-labelledby="h-decision">
          <h2 id="h-decision">Décision</h2>
          <dl className="details">
            <dt>Référence</dt>
            <dd className="mono">{d.reference}</dd>
            <dt>Juridiction</dt>
            <dd>{d.jurisdiction_name}</dd>
            <dt>Date</dt>
            <dd>{formatDay(d.decided_on)}</dd>
            <dt>Catégorie</dt>
            <dd>
              {d.category_label}
              <div className="small muted">{d.category_legal_basis}</div>
            </dd>
            <dt>Nature</dt>
            <dd>{d.nature}</dd>
            <dt>Statut juridique</dt>
            <dd>{LEGAL_STATUS[d.legal_status] ?? d.legal_status}</dd>
            <dt>Source</dt>
            <dd>{d.source_ref}</dd>
          </dl>
        </section>
        <section className="card flow" aria-labelledby="h-personne">
          <h2 id="h-personne">Personne</h2>
          {d.person && (
            <dl className="details">
              <dt>Identité</dt>
              <dd>
                <Link className="lnk" to={`/espace/registre/personnes/${d.person_id}`}>
                  {d.person.surname} {d.person.given_names}
                </Link>
              </dd>
              <dt>Naissance</dt>
              <dd>
                {formatPartialDate({
                  year: d.person.birth_year,
                  month: d.person.birth_month,
                  day: d.person.birth_day,
                })}
                {d.person.birth_place ? `, ${d.person.birth_place}` : ''}
              </dd>
              <dt>Casier tenant</dt>
              <dd>{d.holding_jurisdiction_name}</dd>
              <dt>Préparée par</dt>
              <dd>{d.prepared_by_name}</dd>
              {d.validated_by_name && (
                <>
                  <dt>Validée par</dt>
                  <dd>
                    {d.validated_by_name}, {d.validated_at ? formatDate(d.validated_at) : ''}
                  </dd>
                </>
              )}
              {d.decision_reason && (
                <>
                  <dt>Dernier motif</dt>
                  <dd>{d.decision_reason}</dd>
                </>
              )}
            </dl>
          )}
        </section>
      </div>

      <div className="cols">
        <section className="card flow" aria-labelledby="h-contenu">
          <h2 id="h-contenu">Contenu</h2>
          {current && (
            <div className="card tint" style={{ padding: 18 }}>
              <ContentView content={current.content} />
            </div>
          )}
          <h3>Versions</h3>
          <ol className="tl">
            {d.versions.map((v) => (
              <li key={v.version_no} className={v === current ? 'now' : 'done'}>
                <b>
                  Version {v.version_no} - effet au {formatDay(v.effective_from)}
                  <StatusChip
                    tone={v.status === 'validee' ? 'ok' : v.status === 'proposee' ? 'info' : 'muted'}
                  >
                    {VERSION_STATUS[v.status] ?? v.status}
                  </StatusChip>
                </b>
                <span>
                  {v.recorded_by_name}, {formatDate(v.recorded_at)} - {v.reason}
                  {v.validated_by_name ? ` - validée par ${v.validated_by_name}` : ''}
                </span>
                {d.versions.length > 1 && (
                  <div className="content">
                    <ContentView content={v.content} />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>

        <section className="card flow" aria-labelledby="h-evenements">
          <h2 id="h-evenements">Événements juridiques</h2>
          {d.events.length === 0 ? (
            <div className="empty" style={{ padding: '24px 8px' }}>
              <span className="ic">
                <Icon name="file" size={22} />
              </span>
              <b>Aucun événement.</b>
              <span className="small">
                Les effets des événements sur les bulletins relèvent du référentiel de règles validé.
              </span>
            </div>
          ) : (
            <ol className="tl">
              {d.events.map((e) => (
                <li key={e.id} className={e.status === 'valide' ? 'done' : undefined}>
                  <b>
                    {e.event_label}
                    <StatusChip
                      tone={e.status === 'valide' ? 'ok' : e.status === 'soumis' ? 'info' : 'muted'}
                    >
                      {EVENT_STATUS[e.status] ?? e.status}
                    </StatusChip>
                  </b>
                  <span>
                    Effet au {formatDay(e.effective_date)} - {e.authority} ({e.evidence_ref})
                  </span>
                  {e.status === 'soumis' && canValidate && e.recorded_by !== me?.userId && (
                    <div className="row" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={!reasonOk}
                        onClick={() =>
                          void post(`/registre/evenements/${e.id}/valider`, { reason }, 'Événement validé.')
                        }
                      >
                        Valider
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={!reasonOk}
                        onClick={() =>
                          void post(`/registre/evenements/${e.id}/rejeter`, { reason }, 'Événement rejeté.')
                        }
                      >
                        Rejeter
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
          {d.events.length > 0 && (
            <p className="small muted">
              Les effets des événements sur les bulletins relèvent du référentiel de règles validé.
            </p>
          )}
        </section>
      </div>

      {(d.status === 'soumise' && canValidate) ||
      ((d.status === 'brouillon' || d.status === 'a_corriger') && canPrepare) ||
      (pendingVersion && canValidate) ||
      (d.pendingErasures.length > 0 && canValidate) ? (
        <section className="card flow" aria-labelledby="h-decider">
          <h2 id="h-decider">Décision</h2>
          <div className="field">
            <label htmlFor="reason">Motif</label>
            <textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="actions" style={{ alignItems: 'center' }}>
            {(d.status === 'brouillon' || d.status === 'a_corriger') && canPrepare && (
              <button
                type="button"
                className="btn"
                onClick={() =>
                  void post(
                    `/registre/inscriptions/${d.id}/soumettre`,
                    undefined,
                    'Inscription soumise au validateur.',
                  )
                }
              >
                Soumettre pour validation
              </button>
            )}
            {d.status === 'soumise' && canValidate && (
              <>
                {d.prepared_by === me?.userId && (
                  <span className="small muted">
                    Vous avez préparé cette inscription : un autre validateur doit décider.
                  </span>
                )}
                <button
                  type="button"
                  className="btn"
                  disabled={!reasonOk}
                  onClick={() =>
                    void post(`/registre/inscriptions/${d.id}/valider`, { reason }, 'Inscription validée.')
                  }
                >
                  Valider
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={!reasonOk}
                  onClick={() =>
                    void post(
                      `/registre/inscriptions/${d.id}/retourner`,
                      { reason },
                      'Inscription retournée pour correction.',
                    )
                  }
                >
                  Retourner pour correction
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={!reasonOk}
                  onClick={() =>
                    void post(`/registre/inscriptions/${d.id}/rejeter`, { reason }, 'Inscription rejetée.')
                  }
                >
                  Rejeter
                </button>
              </>
            )}
            {pendingVersion && canValidate && pendingVersion.recorded_by !== me?.userId && (
              <>
                <button
                  type="button"
                  className="btn"
                  disabled={!reasonOk}
                  onClick={() =>
                    void post(
                      `/registre/inscriptions/${d.id}/versions/${pendingVersion.version_no}/valider`,
                      { reason },
                      'Version validée.',
                    )
                  }
                >
                  Valider la version {pendingVersion.version_no}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={!reasonOk}
                  onClick={() =>
                    void post(
                      `/registre/inscriptions/${d.id}/versions/${pendingVersion.version_no}/rejeter`,
                      { reason },
                      'Version rejetée.',
                    )
                  }
                >
                  Rejeter la version {pendingVersion.version_no}
                </button>
              </>
            )}
          </div>
          {d.pendingErasures.length > 0 && canValidate && (
            <div className="alert alert-warn stack">
              {d.pendingErasures.map((e) => (
                <div key={e.id}>
                  <strong>Effacement légal demandé</strong> - fondement : {e.legal_basis}. {e.reason}
                  {e.requested_by !== me?.userId && (
                    <div className="row" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={!reasonOk}
                        onClick={() =>
                          void post(
                            `/registre/effacements/${e.id}/approuver`,
                            { reason },
                            'Effacement exécuté. L’inscription ne subsiste qu’à l’état de pierre tombale.',
                          )
                        }
                      >
                        Approuver et exécuter l’effacement
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={!reasonOk}
                        onClick={() =>
                          void post(`/registre/effacements/${e.id}/refuser`, { reason }, 'Effacement refusé.')
                        }
                      >
                        Refuser
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      ) : null}

      {d.status === 'validee' && (
        <section className="stack" style={{ gap: 12 }} aria-labelledby="actions-inscription">
          <h2 id="actions-inscription" style={{ margin: 0 }}>
            Actions
          </h2>
          <details className="acc">
            <summary>
              <span>
                État à une date
                <small>
                  Ce que le registre retenait à une date d’effet, tel que le système le connaissait à un
                  instant donné.
                </small>
              </span>
            </summary>
            <div className="body stack">
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="as-date">Date d’effet</label>
                  <input
                    id="as-date"
                    type="date"
                    value={asOf.date}
                    onChange={(e) => setAsOf({ ...asOf, date: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="as-known">Connu le (facultatif)</label>
                  <input
                    id="as-known"
                    type="datetime-local"
                    value={asOf.knownAt}
                    onChange={(e) => setAsOf({ ...asOf, knownAt: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <button type="button" className="btn btn-ghost" onClick={() => void viewState()}>
                  Afficher l’état
                </button>
              </div>
              {stateResult && (
                <div className="alert alert-info small">
                  {stateResult.version ? (
                    <>
                      Version {stateResult.version.version_no} applicable.
                      <ContentView content={stateResult.version.content} />
                    </>
                  ) : (
                    'Aucune version validée à cette date.'
                  )}
                  <div>
                    Événements applicables :{' '}
                    {stateResult.events.length
                      ? stateResult.events
                          .map((e) => `${e.label} (${formatDay(e.effective_date)})`)
                          .join(', ')
                      : 'aucun'}
                  </div>
                  <div className="muted">{stateResult.notice}</div>
                </div>
              )}
            </div>
          </details>

          {(canPrepare || can('casier.controler')) && (
            <details className="acc">
              <summary>
                <span>
                  Déclarer un événement
                  <small>Réhabilitation, amnistie, grâce…</small>
                </span>
              </summary>
              <div className="body stack">
                <form
                  className="stack"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void post(
                      `/registre/inscriptions/${d.id}/evenements`,
                      { ...event, decidedOn: event.decidedOn || undefined },
                      'Événement déclaré : il doit être validé par une autre personne.',
                    );
                  }}
                >
                  <div className="field">
                    <label htmlFor="e-type">Type</label>
                    <select
                      id="e-type"
                      value={event.eventType}
                      onChange={(e) => setEvent({ ...event, eventType: e.target.value })}
                    >
                      {(regRefs?.eventTypes ?? []).map((t) => (
                        <option key={t.code} value={t.code}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-grid">
                    <div className="field">
                      <label htmlFor="e-eff">Date d’effet</label>
                      <input
                        id="e-eff"
                        type="date"
                        required
                        value={event.effectiveDate}
                        onChange={(e) => setEvent({ ...event, effectiveDate: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="e-dec">Date de l’acte</label>
                      <input
                        id="e-dec"
                        type="date"
                        value={event.decidedOn}
                        onChange={(e) => setEvent({ ...event, decidedOn: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="e-auth">Autorité</label>
                    <input
                      id="e-auth"
                      required
                      minLength={2}
                      value={event.authority}
                      onChange={(e) => setEvent({ ...event, authority: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="e-proof">Preuve</label>
                    <input
                      id="e-proof"
                      required
                      minLength={3}
                      value={event.evidenceRef}
                      onChange={(e) => setEvent({ ...event, evidenceRef: e.target.value })}
                    />
                  </div>
                  <div>
                    <button className="btn" type="submit">
                      Déclarer
                    </button>
                  </div>
                </form>
              </div>
            </details>
          )}

          {canPrepare && !pendingVersion && current && (
            <details className="acc">
              <summary>
                <span>
                  Corriger (nouvelle version)
                  <small>La version actuelle est conservée ; la correction sera validée par un tiers.</small>
                </span>
              </summary>
              <div className="body stack">
                <form
                  className="stack"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void post(
                      `/registre/inscriptions/${d.id}/versions`,
                      {
                        content: {
                          ...current.content,
                          qualification: correction.qualification || current.content.qualification,
                        },
                        effectiveFrom: correction.effectiveFrom,
                        reason: correction.reason,
                      },
                      'Version proposée : elle doit être validée par une autre personne.',
                    );
                  }}
                >
                  <div className="field">
                    <label htmlFor="k-qual">Qualification corrigée</label>
                    <input
                      id="k-qual"
                      placeholder={current.content.qualification}
                      value={correction.qualification}
                      onChange={(e) => setCorrection({ ...correction, qualification: e.target.value })}
                    />
                  </div>
                  <div className="form-grid">
                    <div className="field">
                      <label htmlFor="k-eff">Date d’effet</label>
                      <input
                        id="k-eff"
                        type="date"
                        required
                        value={correction.effectiveFrom}
                        onChange={(e) => setCorrection({ ...correction, effectiveFrom: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="k-reason">Motif</label>
                      <input
                        id="k-reason"
                        required
                        minLength={5}
                        value={correction.reason}
                        onChange={(e) => setCorrection({ ...correction, reason: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <button className="btn btn-ghost" type="submit">
                      Proposer la version
                    </button>
                  </div>
                </form>
              </div>
            </details>
          )}

          {can('casier.controler') && d.pendingErasures.length === 0 && (
            <details className="acc danger">
              <summary>
                <span>
                  Effacement légal
                  <small>
                    Procédure exceptionnelle à double validation : tout le contenu et le lien avec la personne
                    disparaissent.
                  </small>
                </span>
              </summary>
              <div className="body stack">
                <form
                  className="stack"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void post(
                      '/registre/effacements',
                      { inscriptionId: d.id, ...erasure },
                      'Effacement demandé : un validateur habilité doit l’approuver.',
                    );
                  }}
                >
                  <div className="field">
                    <label htmlFor="er-basis">Fondement (acte, disposition)</label>
                    <input
                      id="er-basis"
                      required
                      minLength={5}
                      value={erasure.legalBasis}
                      onChange={(e) => setErasure({ ...erasure, legalBasis: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="er-reason">Motif</label>
                    <input
                      id="er-reason"
                      required
                      minLength={5}
                      value={erasure.reason}
                      onChange={(e) => setErasure({ ...erasure, reason: e.target.value })}
                    />
                  </div>
                  <div>
                    <button className="btn btn-danger" type="submit">
                      Demander l’effacement
                    </button>
                  </div>
                </form>
              </div>
            </details>
          )}
        </section>
      )}
    </div>
  );
}
